import React, { useState, useEffect } from 'react';
import { Globe, Plus, Edit2, Trash2, Save, X, Search, Filter, Languages } from 'lucide-react';

const TranslationsUI = () => {
  const [translations, setTranslations] = useState([]);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterTable, setFilterTable] = useState('');

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Supported languages
  const languages = [
    { code: 'tr', name: 'Türkçe' },
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'it', name: 'Italiano' },
    { code: 'ru', name: 'Русский' },
    { code: 'ar', name: 'العربية' }
  ];

  // Database tables that can be translated
  const translatableTables = [
    { name: 'Categories', displayName: 'Kategoriler' },
    { name: 'Products', displayName: 'Ürünler' },
    { name: 'ProductAddons', displayName: 'Ürün Eklentileri' },
    { name: 'ProductVariants', displayName: 'Ürün Varyantları' },
    { name: 'Restaurants', displayName: 'Restoranlar' }
  ];

  // Fields that can be translated for each table
  const translatableFields = {
    Categories: ['Name', 'Description'],
    Products: ['Name', 'Description', 'Ingredients'],
    ProductAddons: ['Name', 'Description'],
    ProductVariants: ['Name', 'Description'],
    Restaurants: ['Name', 'Description']
  };

  const [formData, setFormData] = useState({
    restaurantId: '',
    languageCode: 'en',
    tableName: 'Products',
    recordId: '',
    fieldName: 'Name',
    translatedText: ''
  });

  // Load translations from API
  useEffect(() => {
    fetchTranslations();
  }, []);

  const fetchTranslations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/Translations`);
      if (response.ok) {
        const data = await response.json();
        setTranslations(data);
      } else {
        setError('Çeviriler yüklenirken hata oluştu');
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset field name when table changes
    if (name === 'tableName') {
      setFormData(prev => ({
        ...prev,
        fieldName: translatableFields[value]?.[0] || ''
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      restaurantId: '',
      languageCode: 'en',
      tableName: 'Products',
      recordId: '',
      fieldName: 'Name',
      translatedText: ''
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    resetForm();
  };

  const handleEdit = (translation) => {
    setEditingTranslation(translation.translationId);
    setFormData({
      restaurantId: translation.restaurantId,
      languageCode: translation.languageCode,
      tableName: translation.tableName,
      recordId: translation.recordId,
      fieldName: translation.fieldName,
      translatedText: translation.translatedText
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.restaurantId) {
        setError('Restoran ID zorunludur');
        return;
      }

      if (!formData.recordId) {
        setError('Kayıt ID zorunludur');
        return;
      }

      if (!formData.translatedText.trim()) {
        setError('Çeviri metni zorunludur');
        return;
      }

      // Check if translation already exists
      const existingTranslation = translations.find(t => 
        t.restaurantId === formData.restaurantId && 
        t.languageCode === formData.languageCode &&
        t.tableName === formData.tableName &&
        t.recordId === formData.recordId &&
        t.fieldName === formData.fieldName &&
        t.translationId !== editingTranslation
      );

      if (existingTranslation) {
        setError('Bu kayıt için aynı dil ve alan çevirisi zaten mevcut');
        return;
      }

      const requestData = {
        restaurantId: formData.restaurantId,
        languageCode: formData.languageCode,
        tableName: formData.tableName,
        recordId: formData.recordId,
        fieldName: formData.fieldName,
        translatedText: formData.translatedText.trim()
      };

      let response;
      
      if (editingTranslation) {
        // Update existing translation
        response = await fetch(`${API_BASE_URL}/api/Translations/${editingTranslation}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      } else {
        // Create new translation
        response = await fetch(`${API_BASE_URL}/api/Translations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      }

      if (response.ok) {
        // Refresh the list
        await fetchTranslations();
        setIsCreating(false);
        setEditingTranslation(null);
        resetForm();
      } else {
        const errorData = await response.text();
        setError(errorData || 'İşlem sırasında hata oluştu');
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingTranslation(null);
    resetForm();
    setError('');
  };

  const handleDelete = async (translationId) => {
    if (window.confirm('Bu çeviriyi silmek istediğinizden emin misiniz?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/Translations/${translationId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          // Refresh the list
          await fetchTranslations();
        } else {
          setError('Silme işlemi sırasında hata oluştu');
        }
      } catch (err) {
        setError('API bağlantı hatası');
        console.error('API Error:', err);
      }
    }
  };

  const getLanguageName = (code) => {
    return languages.find(lang => lang.code === code)?.name || code.toUpperCase();
  };

  const getTableDisplayName = (tableName) => {
    return translatableTables.find(table => table.name === tableName)?.displayName || tableName;
  };

  const getFieldDisplayName = (fieldName) => {
    const fieldNames = {
      'Name': 'İsim',
      'Description': 'Açıklama',
      'Ingredients': 'İçerikler'
    };
    return fieldNames[fieldName] || fieldName;
  };

  // Filtered translations
  const filteredTranslations = translations.filter(translation => {
    const matchesSearch = !searchTerm || 
      translation.translatedText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      translation.recordId.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLanguage = !filterLanguage || translation.languageCode === filterLanguage;
    const matchesTable = !filterTable || translation.tableName === filterTable;
    
    return matchesSearch && matchesLanguage && matchesTable;
  });

  // Group translations by language for stats
  const translationStats = languages.map(lang => ({
    ...lang,
    count: translations.filter(t => t.languageCode === lang.code).length
  }));

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Çeviriler</h1>
                <p className="text-gray-600">Çoklu dil desteği için çeviri metinlerini yönetin</p>
              </div>
            </div>
            {!isCreating && !editingTranslation && (
              <button
                onClick={handleCreate}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Yeni Çeviri</span>
              </button>
            )}
          </div>

          {/* Language Stats */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {translationStats.map(lang => (
              <div key={lang.code} className="text-center p-2 bg-gray-50 rounded-lg">
                <div className="text-lg font-bold text-blue-600">{lang.count}</div>
                <div className="text-xs text-gray-600">{lang.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        {(isCreating || editingTranslation) && (
          <div className="border-b border-gray-200 p-6 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingTranslation ? 'Çeviriyi Düzenle' : 'Yeni Çeviri Ekle'}
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restoran ID
                </label>
                <input
                  type="text"
                  name="restaurantId"
                  value={formData.restaurantId}
                  onChange={handleInputChange}
                  placeholder="Restoran ID girin"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dil
                </label>
                <select
                  name="languageCode"
                  value={formData.languageCode}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name} ({lang.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tablo
                </label>
                <select
                  name="tableName"
                  value={formData.tableName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {translatableTables.map(table => (
                    <option key={table.name} value={table.name}>
                      {table.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alan
                </label>
                <select
                  name="fieldName"
                  value={formData.fieldName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {translatableFields[formData.tableName]?.map(field => (
                    <option key={field} value={field}>
                      {getFieldDisplayName(field)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kayıt ID
                </label>
                <input
                  type="text"
                  name="recordId"
                  value={formData.recordId}
                  onChange={handleInputChange}
                  placeholder="Çevrilecek kaydın ID'si"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Çeviri Metni
                </label>
                <textarea
                  name="translatedText"
                  value={formData.translatedText}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Çeviri metnini girin..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
                <span>İptal</span>
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="border-b border-gray-200 p-4 bg-gray-50">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Çeviri metni veya ID ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterLanguage}
                onChange={(e) => setFilterLanguage(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tüm Diller</option>
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
              
              <select
                value={filterTable}
                onChange={(e) => setFilterTable(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tüm Tablolar</option>
                {translatableTables.map(table => (
                  <option key={table.name} value={table.name}>
                    {table.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Translations List */}
        <div className="p-6">
          {loading && translations.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Yükleniyor...</p>
            </div>
          ) : filteredTranslations.length === 0 ? (
            <div className="text-center py-12">
              <Languages className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || filterLanguage || filterTable ? 'Arama kriterlerine uygun çeviri bulunamadı' : 'Henüz çeviri tanımlanmamış'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || filterLanguage || filterTable ? 'Farklı filtreler deneyebilirsiniz' : 'İlk çevirinizi ekleyerek başlayın'}
              </p>
              {!searchTerm && !filterLanguage && !filterTable && (
                <button
                  onClick={handleCreate}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Çeviri Ekle
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTranslations.map((translation) => (
                <div key={translation.translationId} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {getLanguageName(translation.languageCode)}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getTableDisplayName(translation.tableName)}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {getFieldDisplayName(translation.fieldName)}
                        </span>
                      </div>
                      
                      <div className="mb-2">
                        <p className="text-gray-900 font-medium">{translation.translatedText}</p>
                      </div>
                      
                      <div className="text-sm text-gray-500 space-y-1">
                        <div><span className="font-medium">Restoran ID:</span> {translation.restaurantId}</div>
                        <div><span className="font-medium">Kayıt ID:</span> {translation.recordId}</div>
                        {translation.createdDate && (
                          <div><span className="font-medium">Oluşturulma:</span> {new Date(translation.createdDate).toLocaleDateString('tr-TR')}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(translation)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(translation.translationId)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslationsUI;